import {
  FOOD_MAX_KCAL_PER_100G,
  FOOD_MEAL_TIMES,
  type AdminFoodDetail,
  type AdminFoodUpdateRequest,
  type CostBandName,
  type FoodCuisineName,
  type FoodDietClassName,
  type FoodMealTimeName,
  type HealthTierName,
  type MealRoleName,
  type PortionUnitName,
} from '@steado/admin-contract/foods';
import type { AllergenName } from '@steado/admin-contract/user-detail';

/**
 * The food editor's state and its rules, kept pure so the two that block a
 * save (a figure out of range, energy and macros more than 10% apart) are
 * tested without a browser.
 *
 * Inputs are held as typed strings: "5." is a number half-typed, and turning
 * it into 5 on every keystroke would fight the person typing it. Only a draft
 * that passes `editProblems` is turned into a request.
 */

export interface PortionDraft {
  /** Stable across renders and edits; the row's id when it has one. */
  readonly key: string;
  readonly id: string | null;
  readonly label: string;
  readonly unit: PortionUnitName;
  readonly grams: string;
  readonly isDefault: boolean;
}

export interface AliasDraft {
  /** The alias's id, or null for one added in this edit. */
  readonly id: string | null;
  readonly alias: string;
  readonly language: string | null;
}

export interface NutritionDraft {
  readonly kcal: string;
  readonly protein: string;
  readonly carbs: string;
  readonly fat: string;
  /** Empty means the source states no fibre figure, which is not zero. */
  readonly fibre: string;
}

export interface EditDraft {
  readonly isCommonInIndia: boolean;
  readonly dietClass: FoodDietClassName;
  readonly hasOnionGarlic: boolean;
  readonly jainExcluded: boolean;
  readonly cuisines: readonly FoodCuisineName[];
  readonly mealRole: MealRoleName | null;
  readonly servableAsIs: boolean;
  readonly prepTimeBand: AdminFoodDetail['prepTimeBand'];
  readonly costBand: CostBandName | null;
  readonly healthTier: HealthTierName | null;
  readonly mealTimes: readonly FoodMealTimeName[];
  readonly seasonMonths: readonly number[];
  readonly nutrition: NutritionDraft;
  readonly displayName: string;
  readonly aliases: readonly AliasDraft[];
  readonly allergens: readonly AllergenName[];
  readonly portions: readonly PortionDraft[];
  readonly note: string;
  readonly sources: readonly string[];
}

/**
 * The figures, as the editor lists them: id, label, unit, highest allowed.
 * Energy's bound is the catalog's own (`FOOD_MAX_KCAL_PER_100G`), so the
 * editor never refuses a figure the server would take.
 */
export const NUTRITION_FIELDS = [
  { id: 'kcal', label: 'Energy', unit: 'kcal', max: FOOD_MAX_KCAL_PER_100G },
  { id: 'protein', label: 'Protein', unit: 'g', max: 100 },
  { id: 'carbs', label: 'Carbohydrate', unit: 'g', max: 100 },
  { id: 'fat', label: 'Fat', unit: 'g', max: 100 },
  { id: 'fibre', label: 'Fibre', unit: 'g', max: 100 },
] as const satisfies readonly { id: keyof NutritionDraft; label: string; unit: string; max: number }[];

/** Grams one portion may weigh, as the design states the range. */
export const PORTION_GRAMS_MIN = 1;
export const PORTION_GRAMS_MAX = 2000;

/** More than this far apart, energy and macros cannot both be right, and Save waits. */
export const MACRO_TOLERANCE = 0.1;

function figure(value: number | null): string {
  return value === null ? '' : String(value);
}

export function draftOf(food: AdminFoodDetail): EditDraft {
  return {
    isCommonInIndia: food.isCommonInIndia,
    dietClass: food.dietClass,
    hasOnionGarlic: food.hasOnionGarlic,
    jainExcluded: food.jainExcluded,
    cuisines: food.cuisines,
    mealRole: food.mealRole,
    servableAsIs: food.servableAsIs,
    prepTimeBand: food.prepTimeBand,
    costBand: food.costBand,
    healthTier: food.healthTier,
    mealTimes: food.mealTimes,
    seasonMonths: food.seasonMonths,
    nutrition: {
      kcal: figure(food.macrosPer100g.kcal),
      protein: figure(food.macrosPer100g.proteinG),
      carbs: figure(food.macrosPer100g.carbsG),
      fat: figure(food.macrosPer100g.fatG),
      fibre: figure(food.macrosPer100g.fibreG),
    },
    displayName: food.displayName ?? food.name,
    aliases: food.aliases.map((alias) => ({ id: alias.id, alias: alias.alias, language: alias.language })),
    allergens: food.allergens,
    portions: food.portions.map((portion) => ({
      key: portion.id,
      id: portion.id,
      label: portion.label,
      unit: portion.unit,
      grams: String(portion.grams),
      isDefault: portion.isDefault,
    })),
    note: '',
    sources: [],
  };
}

/** A typed figure as a number, or null when it is not one. */
export function parseFigure(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

export interface NutritionFieldCheck {
  readonly id: keyof NutritionDraft;
  readonly error: string | null;
  readonly changed: boolean;
}

export interface NutritionCheck {
  readonly fields: readonly NutritionFieldCheck[];
  /** What protein, carbohydrate and fat add up to at 4, 4 and 9 kcal a gram. */
  readonly macroKcal: number;
  readonly kcal: number;
  /** How far energy sits from that, as a share of it. */
  readonly apart: number;
  /** Within 1%: the check passes outright. */
  readonly passes: boolean;
  /** More than 10% apart: Save waits until one of them changes. */
  readonly blocks: boolean;
  /** Protein, carbohydrate, fat and fibre, in grams of the 100. */
  readonly grams: number;
}

export function checkNutrition(food: AdminFoodDetail, nutrition: NutritionDraft): NutritionCheck {
  const before = draftOf(food).nutrition;
  const fields = NUTRITION_FIELDS.map((field): NutritionFieldCheck => {
    const raw = nutrition[field.id];
    const n = parseFigure(raw);
    const optional = field.id === 'fibre' && raw.trim() === '';
    let error: string | null = null;
    if (!optional && n === null) error = 'Enter a number';
    else if (n !== null && (n < 0 || n > field.max)) error = `Must be 0 to ${String(field.max)}`;
    return { id: field.id, error, changed: parseFigure(before[field.id]) !== n };
  });
  const n = (id: keyof NutritionDraft) => parseFigure(nutrition[id]) ?? 0;
  const macroKcal = n('protein') * 4 + n('carbs') * 4 + n('fat') * 9;
  const kcal = n('kcal');
  const apart = macroKcal === 0 ? 0 : Math.abs(kcal - macroKcal) / macroKcal;
  return {
    fields,
    macroKcal,
    kcal,
    apart,
    passes: apart < 0.01,
    blocks: apart > MACRO_TOLERANCE,
    grams: n('protein') + n('carbs') + n('fat') + n('fibre'),
  };
}

export function nutritionChanged(food: AdminFoodDetail, draft: EditDraft): boolean {
  return checkNutrition(food, draft.nutrition).fields.some((field) => field.changed);
}

export interface PortionCheck {
  readonly key: string;
  readonly error: string | null;
  readonly gramsError: boolean;
}

export function checkPortions(portions: readonly PortionDraft[]): PortionCheck[] {
  const seen = new Set<string>();
  return portions.map((portion) => {
    const grams = parseFigure(portion.grams);
    const label = portion.label.trim();
    const gramsError = grams === null || grams < PORTION_GRAMS_MIN || grams > PORTION_GRAMS_MAX;
    let error: string | null = null;
    if (gramsError) error = 'Grams must be 1 to 2,000';
    else if (label === '') error = 'Give the portion a name';
    else if (/^[0-9]/.test(label)) error = 'Write the name without a number, such as katori';
    else if (seen.has(label.toLowerCase())) error = 'Two portions have this name';
    seen.add(label.toLowerCase());
    return { key: portion.key, error, gramsError };
  });
}

function sameList<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function sameSet<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}

function portionsChanged(food: AdminFoodDetail, portions: readonly PortionDraft[]): boolean {
  if (portions.length !== food.portions.length) return true;
  return portions.some((portion, index) => {
    const was = food.portions[index];
    if (was === undefined) return true;
    return (
      portion.id !== was.id ||
      portion.label.trim() !== was.label ||
      portion.unit !== was.unit ||
      parseFigure(portion.grams) !== was.grams ||
      portion.isDefault !== was.isDefault
    );
  });
}

/**
 * What would block Save, as a count the bar names: "Fix 2 values to save".
 * Only what this edit touches counts, so a food whose stored figures already
 * disagree can still have its names fixed.
 */
export function editProblems(food: AdminFoodDetail, draft: EditDraft): string[] {
  const problems: string[] = [];
  if (nutritionChanged(food, draft)) {
    const check = checkNutrition(food, draft.nutrition);
    for (const field of check.fields) {
      if (field.error !== null) problems.push(field.id);
    }
    if (check.blocks) problems.push('energy');
    if (check.grams > 100) problems.push('total grams');
  }
  if (portionsChanged(food, draft.portions)) {
    for (const portion of checkPortions(draft.portions)) {
      if (portion.error !== null) problems.push(`portion ${portion.key}`);
    }
    if (draft.portions.length > 0 && draft.portions.filter((portion) => portion.isDefault).length !== 1) {
      problems.push('default portion');
    }
  }
  if (draft.displayName.trim() === '') problems.push('display name');
  return problems;
}

export interface DiffLine {
  readonly label: string;
  readonly before: string;
  readonly after: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function words(value: string | null): string {
  if (value === null) return 'none';
  const text = value.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function listText(values: readonly string[]): string {
  return values.length === 0 ? 'none' : words(values.join(', '));
}

function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

function figureText(raw: string, unit: string): string {
  const n = parseFigure(raw);
  return n === null ? 'none' : `${raw.trim()} ${unit}`;
}

/** What the save changes, line by line, as "What changes" lists it before saving. */
export function diffOf(food: AdminFoodDetail, draft: EditDraft): DiffLine[] {
  const was = draftOf(food);
  const lines: DiffLine[] = [];
  const push = (label: string, before: string, after: string) => {
    lines.push({ label, before, after });
  };

  if (draft.displayName.trim() !== was.displayName)
    push('Display name', was.displayName, draft.displayName.trim());
  for (const field of NUTRITION_FIELDS) {
    if (parseFigure(was.nutrition[field.id]) !== parseFigure(draft.nutrition[field.id])) {
      push(
        `${field.label} per 100 g`,
        figureText(was.nutrition[field.id], field.unit),
        figureText(draft.nutrition[field.id], field.unit),
      );
    }
  }
  const names = (aliases: readonly AliasDraft[]) => aliases.map((alias) => alias.alias);
  if (!sameList(names(was.aliases), names(draft.aliases))) {
    push('Other names', names(was.aliases).join(', ') || 'none', names(draft.aliases).join(', ') || 'none');
  }
  if (!sameSet(was.allergens, draft.allergens))
    push('Allergens', listText(was.allergens), listText(draft.allergens));

  for (const portion of draft.portions) {
    const before = food.portions.find((candidate) => candidate.id === portion.id);
    if (before === undefined) {
      push(`Portion added, 1 ${portion.label.trim()}`, 'none', `${portion.grams.trim()} g`);
    } else if (before.label !== portion.label.trim() || before.unit !== portion.unit) {
      push(
        `Portion, 1 ${before.label}`,
        `1 ${before.label}, ${String(before.grams)} g`,
        `1 ${portion.label.trim()}, ${portion.grams.trim()} g`,
      );
    } else if (before.grams !== parseFigure(portion.grams)) {
      push(
        `${before.isDefault ? 'Default portion' : 'Portion'}, 1 ${before.label}`,
        `${String(before.grams)} g`,
        `${portion.grams.trim()} g`,
      );
    }
  }
  for (const portion of food.portions) {
    if (!draft.portions.some((candidate) => candidate.id === portion.id)) {
      push(`Portion removed, 1 ${portion.label}`, `${String(portion.grams)} g`, 'none');
    }
  }
  const defaultOf = (portions: readonly { label: string; isDefault: boolean }[]) =>
    portions.find((portion) => portion.isDefault)?.label.trim() ?? null;
  const wasDefault = defaultOf(food.portions);
  const nowDefault = defaultOf(draft.portions);
  const sameRow =
    food.portions.find((portion) => portion.isDefault)?.id ===
    draft.portions.find((portion) => portion.isDefault)?.id;
  if (!sameRow && (wasDefault !== null || nowDefault !== null)) {
    push(
      'Default portion',
      wasDefault === null ? 'none' : `1 ${wasDefault}`,
      nowDefault === null ? 'none' : `1 ${nowDefault}`,
    );
  }

  if (draft.isCommonInIndia !== was.isCommonInIndia) {
    push('Everyday food in India', yesNo(was.isCommonInIndia), yesNo(draft.isCommonInIndia));
  }
  if (draft.dietClass !== was.dietClass) push('Diet class', words(was.dietClass), words(draft.dietClass));
  if (draft.hasOnionGarlic !== was.hasOnionGarlic) {
    push('Contains onion or garlic', yesNo(was.hasOnionGarlic), yesNo(draft.hasOnionGarlic));
  }
  if (draft.jainExcluded !== was.jainExcluded)
    push('Jain avoids', yesNo(was.jainExcluded), yesNo(draft.jainExcluded));
  if (!sameSet(was.cuisines, draft.cuisines))
    push('Cuisines', listText(was.cuisines), listText(draft.cuisines));
  if (draft.mealRole !== was.mealRole) push('Meal role', words(was.mealRole), words(draft.mealRole));
  if (draft.servableAsIs !== was.servableAsIs)
    push('Eaten as it is', yesNo(was.servableAsIs), yesNo(draft.servableAsIs));
  if (draft.prepTimeBand !== was.prepTimeBand)
    push('Prep time', words(was.prepTimeBand), words(draft.prepTimeBand));
  if (draft.costBand !== was.costBand) push('Cost', words(was.costBand), words(draft.costBand));
  if (draft.healthTier !== was.healthTier)
    push('Health tier', words(was.healthTier), words(draft.healthTier));
  if (!sameList(was.mealTimes, draft.mealTimes))
    push('Meal times', listText(was.mealTimes), listText(draft.mealTimes));
  if (!sameList(was.seasonMonths, draft.seasonMonths)) {
    const months = (values: readonly number[]) =>
      values.length === 0 ? 'none' : values.map((m) => MONTHS[m - 1] ?? String(m)).join(', ');
    push('Months in season', months(was.seasonMonths), months(draft.seasonMonths));
  }
  return lines;
}

/** Only what moved, so History and the audit row say exactly what this save did. */
export function patchOf(food: AdminFoodDetail, draft: EditDraft): AdminFoodUpdateRequest {
  const was = draftOf(food);
  const patch: AdminFoodUpdateRequest = {};
  if (draft.isCommonInIndia !== was.isCommonInIndia) patch.isCommonInIndia = draft.isCommonInIndia;
  if (!sameSet(was.cuisines, draft.cuisines)) patch.cuisines = [...draft.cuisines];
  if (draft.dietClass !== was.dietClass) patch.dietClass = draft.dietClass;
  if (draft.hasOnionGarlic !== was.hasOnionGarlic) patch.hasOnionGarlic = draft.hasOnionGarlic;
  if (draft.jainExcluded !== was.jainExcluded) patch.jainExcluded = draft.jainExcluded;
  if (draft.mealRole !== was.mealRole) patch.mealRole = draft.mealRole;
  if (draft.servableAsIs !== was.servableAsIs) patch.servableAsIs = draft.servableAsIs;
  if (draft.prepTimeBand !== was.prepTimeBand) patch.prepTimeBand = draft.prepTimeBand;
  if (draft.costBand !== was.costBand) patch.costBand = draft.costBand;
  if (draft.healthTier !== was.healthTier) patch.healthTier = draft.healthTier;
  if (!sameList(was.mealTimes, draft.mealTimes)) {
    patch.mealTimes = FOOD_MEAL_TIMES.filter((slot) => draft.mealTimes.includes(slot));
  }
  if (!sameList(was.seasonMonths, draft.seasonMonths))
    patch.seasonMonths = [...draft.seasonMonths].sort((a, b) => a - b);
  if (nutritionChanged(food, draft)) {
    patch.nutrition = {
      kcalPer100g: parseFigure(draft.nutrition.kcal) ?? 0,
      proteinGPer100g: parseFigure(draft.nutrition.protein) ?? 0,
      carbsGPer100g: parseFigure(draft.nutrition.carbs) ?? 0,
      fatGPer100g: parseFigure(draft.nutrition.fat) ?? 0,
      fibreGPer100g: parseFigure(draft.nutrition.fibre),
    };
  }
  if (draft.displayName.trim() !== was.displayName) patch.displayName = draft.displayName.trim();
  const removed = was.aliases.filter((alias) => !draft.aliases.some((kept) => kept.id === alias.id));
  const added = draft.aliases.filter((alias) => alias.id === null);
  if (removed.length > 0 || added.length > 0) {
    patch.aliases = {
      add: added.map((alias) => ({ alias: alias.alias, language: alias.language })),
      remove: removed.flatMap((alias) => (alias.id === null ? [] : [alias.id])),
    };
  }
  if (!sameSet(was.allergens, draft.allergens)) patch.allergens = [...draft.allergens];
  if (portionsChanged(food, draft.portions)) {
    patch.portions = draft.portions.map((portion) => ({
      id: portion.id,
      label: portion.label.trim(),
      unit: portion.unit,
      grams: parseFigure(portion.grams) ?? 0,
      isDefault: portion.isDefault,
    }));
  }
  if (draft.note.trim() !== '') patch.note = draft.note.trim();
  if (draft.sources.length > 0) patch.sources = [...draft.sources];
  return patch;
}
